import React, { Component } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import moment from 'moment'
import { Form } from 'formsy-react'
import { Colors, Select, Button, TextInput, Icon } from 'advenio-components'
import { isEmpty, isNil } from 'ramda'
import { stringContainsExactlyOf, toPlainObject, userHasRole } from '../../../utils/Utils'
import { BACK_OFFICE_CENTER_ROLES } from '../../../Constants'

const EvaluationDimentionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 98%;
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  margin-top: 16px;
  box-sizing: border-box;
  overflow-x: auto;
`

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  align-items: center;
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  background-color: ${({ color }) => color || '#E88697'};
`

const DimentionTitle = styled.h2`
  font-size: 24px;
  font-weight: bold;
  color: white;
  margin: 0;
`

const LastUpdateText = styled.div`
  font-size: 14px;
  color: #6B46C1;
`

const ObjectiveSection = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  border-radius: 15px;
  border: 1px solid ${({ color }) => color || '#E88697'};
  align-items: center;
  justify-content: center;
  display: flex;
  margin: 0 35px;
`

const ObjectiveLabel = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: ${Colors.gray700};
  margin-bottom: 8px;
`

const ObjectiveText = styled.div`
  font-size: 16px;
  line-height: 1.5;
`

const SectionContainer = styled.div`
  margin-bottom: 24px;
  width: 100%;
  overflow-x: auto;
  /* Add border to all except the first SectionContainer */
  & + & {
    border-top: 3px solid ${({ color }) => color || '#E88697'};
  }
`

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: ${({ color }) => color || '#E88697'};
  margin-bottom: 16px;
  margin: 35px 35px 0 35px;
`

const CriteriaTable = styled.table`
  width: 100%;
  min-width: 600px;
  border-collapse: collapse;
  margin-bottom: 16px;
  table-layout: fixed;
`

const TableHeader = styled.thead`
  color: #000000;
`

const TableHeaderRow = styled.tr`
  height: 60px;
  border-bottom: 3px solid ${Colors.gray200};
`

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: center;
  font-weight: bold;
  white-space: nowrap;
  &:first-child {
    text-align: left;
    width: 45%;
    min-width: 200px;
  }
  &:not(:first-child) {
    width: 18.33%;
    min-width: 120px;
  }
`

const TableBody = styled.tbody``

const TableRow = styled.tr`
  border-bottom: 3px solid ${Colors.gray200};

  &:last-child {
    border-bottom: none;
  }
`

const CriteriaCell = styled.td`
  padding: 16px 8px;
  font-size: 14px;
  color: ${Colors.gray700};
  word-wrap: break-word;
  overflow-wrap: break-word;
  width: 45%;
`

const CycleCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  width: 18.33%;
  vertical-align: middle;
`

const SelectContainer = styled.div`
  width: 100%;
  max-width: 150px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 auto;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : ''};
  justify-content: center;

  > div {
    flex: 1;
    min-width: 0;
  }
`

const ObservationsContainer = styled.div`
  margin-top: 24px;
  margin-bottom: 16px;
`

const ObservationsLabel = styled.label`
  display: block;
  font-weight: bold;
  font-size: 14px;
  color: ${Colors.gray700};
  margin-bottom: 8px;
`

const ObservationsTextArea = styled(TextInput)`
  width: 100%;
  min-height: 100px;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`

const SaveButton = styled(Button)`
  background-color: #6B46C1;
  color: white;
  padding: 12px 32px;
  font-weight: bold;

  &:hover {
    background-color: #553C9A;
  }
`

const WarningBox = styled.div`
  background-color: #F3DFF7;
  border: 1px solid #BB4DD1;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  color: ${Colors.gray700};
  font-size: 14px;
`

const ContainerResult = styled.div`
  ${({ disabled }) => disabled && `
    background-color: ${Colors.gray200};
    border-radius: 8px;
  `}
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
`

const ContainerResultIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 8px;
`

const ContainerResultText = styled.span`
  font-size: 14px;
  color: ${({ disabled }) => disabled ? Colors.gray500 : Colors.gray700};
`

const ObservationsTooltip = styled.div`
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`
const Tooltip = styled.div`
  margin-right: 8px;
  font-size: 12px;
  color: ${Colors.gray700};
  font-style: italic;
  font-weight: 500;
`

// Estados disponibles para los ciclos
const CYCLE_STATES = [
  { id: 1, value: 1, label: 'Inició', color: '#4AC1E0', icon: 'I' },
  { id: 2, value: 2, label: 'En Progreso', color: '#8F7700', icon: 'P' },
  { id: 3, value: 3, label: 'Logrado', color: '#BB4DD1', icon: 'L' },
]

class EvaluationDimention extends Component {
  state = {
    formData: {},
    comments: '',
    cyclesEnabled: [],
    countCycles: 0
  }

  componentWillUnmount() {
    this.setState({ comments: '', formData: {}, countCycles: 0, cyclesEnabled: [] })
  }

  componentDidMount() {
    const { formData, countCycles, observation } = this.props
    this.setState({ formData, countCycles, comments: observation || '' })
    this.getEnabledCycles()
  }

  componentWillReceiveProps(nextProps) {
    const { formData, countCycles, observation } = nextProps
    this.setState({ formData, countCycles, comments: observation || '' })
  }

  /*componentDidUpdate(prevProps) {
    if (prevProps.dimention.id !== this.props.dimention.id) {
      this.initializeFormData()
    }
  }*/


  /*initializeFormData = () => {
    const { dimention } = this.props
    if (isEmpty(dimention) || isNil(dimention.section)) {
      return
    }

    const formData = {}
    let counterCycles = 0;
    dimention.section.forEach(section => {
      section.questions.forEach(question => {
        if (counterCycles < question.answers.length) {
          counterCycles = question.answers.length;
        }
        question.answers.forEach(answer => {
          // Usar el ID del answer como clave única
          const key = `answer_${answer.id}`
          formData[key] = (answer.result && answer.result.id) ? answer.result.id : null
        })
      })
    })
    this.setState({ formData, countCycles: counterCycles || 0, observation: dimention.observation || '' })
  }*/

  getEnabledCycles = () => {
    const { cycles, programId, currentProgram, user } = this.props
    const now = moment();
    const cyclesEnabled = cycles.filter(cycle => {
      if(userHasRole(user,BACK_OFFICE_CENTER_ROLES)){
        return true
      }
      if(currentProgram > programId) {
        
        return false
      }
      if(!!stringContainsExactlyOf(programId.toString(), ['1', '2', '3', '4'])) {
        return true;
      }
      return now.isSameOrAfter(moment(cycle.start_date)) && now.isSameOrBefore(moment(cycle.end_date));
    }).map(cycle => cycle.name);
    this.setState({ cyclesEnabled });
  }

  handleCycleResultChange = (keyAnswer, value) => {
    const { formData } = this.state
    formData [keyAnswer] = value ? parseInt(value) : null
    this.setState({ formData })
  }

  handleObservationsChange = ({target:{value}}) => {
    this.setState({ comments: value })
  }

  handleSave = () => {
    const { formData, comments } = this.state
    const { dimention, onSave } = this.props
    // Preparar datos para enviar
    const saveData = {
      dimentionId: dimention.id,
      results: formData,
      comments,
    }

    if (onSave) {
      onSave(saveData)
    } else {
      console.log('Datos a guardar:', saveData)
    }
  }

  getCycleStateOptions = () => {
    return [
      { id: 0, value: '', label: 'Elegir estado' },
      ...CYCLE_STATES.map(state => ({
        id: state.id,
        value: state.id,
        label: state.label,
      })),
    ]
  }

  getStateLabel = (stateId, keyAnswer, disabled) => {
    const state = CYCLE_STATES.find(s => s.id === stateId)
    if (!state) return null

    return (
      <ContainerResult disabled={disabled} onClick={() => disabled ? null : this.handleCycleResultChange(keyAnswer, false)}>
        <ContainerResultIcon style={{
          backgroundColor: state.color === '#4AC1E0' ? '#B9E8F3' :
                            state.color === '#8F7700' ? '#FFF1AD' :
                            state.color === '#BB4DD1' ? '#F3DFF7' : '#B9E8F3',
          color: state.color,
        }}>
          {state.icon}
        </ContainerResultIcon>
        <ContainerResultText disabled={disabled}>{state.label}</ContainerResultText>
      </ContainerResult>
    )
  }

  getFormatCycleValue = (programId, cycleId) =>{
    let name = cycleId
    if (programId === 2){
      name = cycleId + 6
    }
    if (programId === 3){
      name = cycleId + 12
    }
    return name
  }

  render() {
    const { dimention, cyclesResults, cycleSelected, showCycles, programId } = this.props
    const { formData, cyclesEnabled, countCycles, comments } = this.state
    if (isEmpty(dimention) || isNil(dimention.section) && isEmpty(cyclesResults)) {
      return null
    }

    return (
      <EvaluationDimentionContainer>
        <HeaderContainer color={dimention.color}>
          <DimentionTitle>{dimention.translation || dimention.name}</DimentionTitle>
        </HeaderContainer>

        {dimention.description && (
          <ObjectiveSection color={dimention.color}>
            <ObjectiveText>Objetivo: {dimention.description}</ObjectiveText>
          </ObjectiveSection>
        )}

        <Form style={{ width: '100%' }}>
          {dimention.section.map((section, sectionIndex) => (
            <SectionContainer color={dimention.color} key={sectionIndex}>
              <SectionTitle color={dimention.color}>{section.name}</SectionTitle>
              <CriteriaTable>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell></TableHeaderCell>
                    {[...Array(countCycles)].map((_, index) => {
                      const cycleId = index + 1;
                      const value = programId < 4 ? this.getFormatCycleValue(programId,cycleId) : cycleId
                      const label = programId > 3 ? 'Ciclo' : 'Mes'
                      if (showCycles) {
                        // Only show the selected cycle
                        if (cycleId !== Number(cycleSelected)) return null;
                        return (
                          <TableHeaderCell key={cycleId}>{label} {value}</TableHeaderCell>
                        );
                      } else {
                        // Show all cycles
                        return (
                          <TableHeaderCell key={cycleId}>{label} {value}</TableHeaderCell>
                        );
                      }
                    })}
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {section.questions.map((question, questionIndex) => {
                    // Obtener los ciclos ordenados (1, 2, 3)
                    let answers = question.answers || []
                    //.sort((a, b) => a.id - b.id)
                    if(showCycles) {
                      answers = answers.filter(answer => answer.cycle.id === Number(cycleSelected))
                    }
                    return (
                      <TableRow key={questionIndex}>
                        <CriteriaCell>{question.name}</CriteriaCell>
                        {answers.map(answer => {
                          const keyAnswer = answer ? answer.id : null
                          const disabled = !cyclesEnabled.includes(answer.cycle.name)
                          let currentValue = null;
                          if (keyAnswer) {
                            currentValue = formData[keyAnswer];
                          }
                          return (
                            <CycleCell key={answer.id}>
                              {answer ? (
                                <SelectContainer disabled={disabled}>
                                  {isNil(currentValue) && <Select
                                    name={`${keyAnswer}`}
                                    value={currentValue}
                                    onChangeCallback={({ target: { value } }) =>
                                      this.handleCycleResultChange(keyAnswer, value)
                                    }
                                    options={this.getCycleStateOptions()}
                                    emptyElement={false}
                                    disabled={disabled}
                                  />}
                                  {currentValue && (
                                    this.getStateLabel(parseInt(currentValue), keyAnswer, disabled)
                                  )}
                                </SelectContainer>
                              ) : (
                                <span style={{ color: Colors.gray400 }}>-</span>
                              )}
                            </CycleCell>
                          )
                        })}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </CriteriaTable>
            </SectionContainer>
          ))}

          <ObservationsContainer>
            <ObservationsLabel>Observaciones:</ObservationsLabel>
            <ObservationsTooltip>
              <Tooltip>
                Este espacio busca acompañar y alentar el desarrollo del niño. Aquí describimos sus logros, preferencias, intereses y experiencias significativas que dan contexto a los indicadores logrados. No es un espacio para juicios ni para señalar lo que aún no ha alcanzado.
              </Tooltip>
            </ObservationsTooltip>
            <TextInput
              name="comments"
              value={comments}
              onChangeCallback={this.handleObservationsChange}
              textarea
              placeholder="Ingresa tus observaciones aquí..."
            />
          </ObservationsContainer>

          <ButtonContainer>
            <SaveButton onClick={this.handleSave}>
              Guardar
            </SaveButton>
          </ButtonContainer>

          <WarningBox>
            Favor de revisar bien tus elecciones, ya que cuando selecciones 'guardar' ya no se podrá editar.
          </WarningBox>
        </Form>
      </EvaluationDimentionContainer>
    )
  }
}

EvaluationDimention.propTypes = {
  dimention: PropTypes.object.isRequired,
  onSave: PropTypes.func,
}

EvaluationDimention.defaultProps = {
  onSave: null,
  dimention: {},
  cyclesResults: [],
  cycles: [],
  comments: '',
}

const mapStateToProps = state => ({
  cycles: state.evaluation.cycles.results,
  user: state.auth.user,
})

  export default connect(mapStateToProps)(EvaluationDimention)


// WEBPACK FOOTER //
// ./src/Components/ProfileViews/Evaluation/EvaluationDimention.jsx